import Papa from 'papaparse';
import { stepsOf } from './surveyResults.js';
import type { SurveyResultsDto } from '../types/surveyResults.js';
import type { QuestionStep } from '../types/survey.js';
import type { SurveyAnswerValue } from '../types/surveyResponse.js';

// Phase 6B E7 — CSV export (§6.7). Pure (no DOM): one row per SUBMITTED
// response; fixed identity columns + one column per question (rating adds a
// separate comment column). papaparse handles quoting/escaping. The page wraps
// the returned string in a Blob download.

const RESERVED_OTHER = '__other__';

// CSV cell value — like the read-only formatter, but rating is the bare number
// (its comment goes in a dedicated column).
function csvCell(step: QuestionStep, v: SurveyAnswerValue): string {
  switch (v.type) {
    case 'yes_no': {
      const cfg = step.config.type === 'yes_no' ? step.config : { trueLabel: undefined, falseLabel: undefined };
      return v.value ? cfg.trueLabel || 'Yes' : cfg.falseLabel || 'No';
    }
    case 'multiple_choice_single': {
      const cfg = step.config.type === 'multiple_choice_single' ? step.config : { options: [] };
      if (v.optionId === RESERVED_OTHER) return `Other: ${v.otherText ?? ''}`.trim();
      return cfg.options.find((o) => o.id === v.optionId)?.label ?? '';
    }
    case 'multiple_choice_multi': {
      const cfg = step.config.type === 'multiple_choice_multi' ? step.config : { options: [] };
      return v.optionIds
        .map((id) => (id === RESERVED_OTHER ? `Other: ${v.otherText ?? ''}`.trim() : (cfg.options.find((o) => o.id === id)?.label ?? id)))
        .join(', ');
    }
    case 'short_text':
    case 'long_text':
      return v.text;
    case 'rating_scale':
      return String(v.rating);
    case 'date':
      return v.date;
    case 'number':
      return Number.isNaN(v.number) ? '' : String(v.number);
    case 'name':
      return v.name;
  }
}

export function buildSurveyCsv(dto: SurveyResultsDto): string {
  const questionSteps = stepsOf(dto).filter((s): s is QuestionStep => s.kind === 'question');
  const recipientName = new Map(dto.recipients.map((r) => [r.id, r.displayName]));

  const fields = ['Recipient', 'Account', 'Brand', 'Account manager', 'Submitted'];
  for (const s of questionSteps) {
    fields.push(s.prompt || s.id);
    if (s.type === 'rating_scale') fields.push(`${s.prompt || s.id} (comment)`);
  }

  const submitted = dto.responses.filter((r) => r.submittedAt !== null);
  const data = submitted.map((r) => {
    const byStep = new Map(r.answers.map((a) => [a.stepId, a]));
    const row: string[] = [
      recipientName.get(r.recipientId) ?? '',
      r.accountNameSnapshot,
      r.brandNameSnapshot,
      r.accountManagerNameSnapshot ?? '',
      r.submittedAt ?? '',
    ];
    for (const s of questionSteps) {
      const a = byStep.get(s.id);
      row.push(a ? csvCell(s, a.value) : '');
      if (s.type === 'rating_scale') row.push(a?.comment ?? '');
    }
    return row;
  });

  return Papa.unparse({ fields, data });
}

// CSV-safe filename stem from the survey title.
export function csvFilename(title: string): string {
  const stem = (title || 'survey').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'survey';
  return `${stem}-responses.csv`;
}
